/**
 * What happened on a hole, for Tibbs (docs/TIBBS-VOICE.md 3.1). Pure: computed
 * from the state at `holeOver` / `gameOver` with the engine's own scorer, so
 * every device at a table computes the same facts.
 */
import { scoreGrid } from '../../engine';
import type { GameEvent, GameState } from '../../engine';

export interface PlayerHole {
  seat: number;
  score: number;
  /** Regulation totals; equal before and after on a playoff hole. */
  totalBefore: number;
  totalAfter: number;
  /** 1 = leading. Shared totals share a rank. */
  rankBefore: number;
  rankAfter: number;
  wentOut: boolean;
  /** Columns of matched −5s. */
  eurekaPairs: number;
  /** Largest matched-column bonus group (0 when none). */
  bonusK: number;
  /** The highest matched value among 10, 11, 12 columns (null when none). */
  bigPair: number | null;
  /** Counted values of 10 or more among the cards turned over at the end. */
  caughtValues: number[];
  skips: number;
}

export interface HoleFacts {
  hole: number;
  totalHoles: number;
  playoff: boolean;
  /** World 1..9 the hole was played at. */
  world: number;
  /** 1 on the first trip round the nine courses, 2 on the second; playoffs count as lap 2. */
  lap: number;
  /** Regulation holes still to play after this one. */
  holesLeft: number;
  /** World of the next hole (null when the game is over). */
  nextWorld: number | null;
  /** A sudden-death hole follows: 'start' after regulation, 'again' after a tied playoff hole. */
  playoffNext: 'start' | 'again' | null;
  gameOver: boolean;
  players: PlayerHole[];
  low: number;
  lowSeats: number[];
  reshuffles: number;
  /** Seats with the lowest regulation total before / after this hole. */
  leadersBefore: number[];
  leadersAfter: number[];
  /** Second-best total minus the best (0 when the lead is shared). */
  gap12: number;
  /** Worst total minus the best. */
  gapLast: number;
  /** Seats with the worst total after this hole. */
  lastSeats: number[];
  playerCount: number;
}

const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);

function worldOf(hole: number, totalHoles: number): number {
  if (hole > totalHoles) return 9;
  return ((Math.max(1, hole) - 1) % 9) + 1;
}

/** Rank by total: 1 + how many are strictly lower. */
export function ranks(totals: readonly number[]): number[] {
  return totals.map((t) => 1 + totals.filter((o) => o < t).length);
}

/** Events since the current hole started (the log is capped, so only the tail of a long hole may be there). */
function holeEvents(state: GameState): GameEvent[] {
  const out: GameEvent[] = [];
  for (let i = state.log.length - 1; i >= 0; i--) {
    const e = state.log[i]!;
    if (e.type === 'holeStarted') break;
    out.push(e);
  }
  return out.reverse();
}

/**
 * Facts for the hole that just ended. `state.phase` must be 'holeOver' or
 * 'gameOver' (all cards are face up then, even in a redacted view).
 */
export function holeFacts(state: GameState): HoleFacts {
  const hole = state.hole;
  const playoff = hole > state.totalHoles;
  const events = holeEvents(state);
  const outEv = events.find((e) => e.type === 'wentOut');
  const wentOutBy = outEv && outEv.type === 'wentOut' ? outEv.player : state.wentOutBy;
  const revealedEv = [...events].reverse().find((e) => e.type === 'revealed');
  const revealed = revealedEv && revealedEv.type === 'revealed' ? revealedEv.cards : [];

  const totalsAfter = state.players.map((p) => sum(p.holeScores));
  const totalsBefore = state.players.map((p) => (playoff ? sum(p.holeScores) : sum(p.holeScores.slice(0, -1))));
  const rb = ranks(totalsBefore);
  const ra = ranks(totalsAfter);

  const players: PlayerHole[] = [];
  for (const seat of state.active) {
    const p = state.players[seat]!;
    if (p.grid.length !== 8) continue;
    const sb = scoreGrid(p.grid, { revealAll: true, options: state.options });
    const score = playoff ? (p.playoffScores[p.playoffScores.length - 1] ?? sb.total) : (p.holeScores[p.holeScores.length - 1] ?? sb.total);
    const pairCols = new Set(sb.columns.filter((c) => c.kind === 'pair' || c.kind === 'minusFivePair').map((c) => c.column));
    const bigs = sb.columns.filter((c) => c.kind === 'pair' && (c.top ?? 0) >= 10).map((c) => c.top as number);
    const caughtValues = revealed
      .filter((r) => r.player === seat && r.card.value >= 10 && !pairCols.has(r.slot % 4))
      .map((r) => r.card.value);
    players.push({
      seat,
      score,
      totalBefore: totalsBefore[seat]!,
      totalAfter: totalsAfter[seat]!,
      rankBefore: rb[seat]!,
      rankAfter: ra[seat]!,
      wentOut: wentOutBy === seat,
      eurekaPairs: sb.columns.filter((c) => c.kind === 'minusFivePair').length,
      bonusK: Math.max(0, ...sb.bonuses.map((b) => b.k)),
      bigPair: bigs.length ? Math.max(...bigs) : null,
      caughtValues,
      skips: p.skipsThisHole,
    });
  }

  const low = Math.min(...players.map((p) => p.score));
  const lowSeats = players.filter((p) => p.score === low).map((p) => p.seat);
  const best = Math.min(...totalsAfter);
  const worst = Math.max(...totalsAfter);
  const bestBefore = Math.min(...totalsBefore);
  const sortedTotals = [...totalsAfter].sort((a, b) => a - b);
  const gameOver = state.phase === 'gameOver';
  const contenders = state.playoffPlayers;
  const playoffNext = !gameOver && contenders && contenders.length > 1 ? (playoff ? 'again' : 'start') : null;
  const holesLeft = playoff ? 0 : Math.max(0, state.totalHoles - hole);
  const nextWorld = gameOver ? null : playoffNext ? 9 : worldOf(hole + 1, state.totalHoles);

  return {
    hole,
    totalHoles: state.totalHoles,
    playoff,
    world: worldOf(hole, state.totalHoles),
    lap: playoff ? 2 : hole > 9 ? 2 : 1,
    holesLeft,
    nextWorld,
    playoffNext,
    gameOver,
    players,
    low,
    lowSeats,
    reshuffles: state.reshuffles,
    leadersBefore: totalsBefore.map((t, i) => (t === bestBefore ? i : -1)).filter((i) => i >= 0),
    leadersAfter: totalsAfter.map((t, i) => (t === best ? i : -1)).filter((i) => i >= 0),
    gap12: (sortedTotals[1] ?? best) - best,
    gapLast: worst - best,
    lastSeats: totalsAfter.map((t, i) => (t === worst ? i : -1)).filter((i) => i >= 0),
    playerCount: state.players.length,
  };
}
